import { useState } from "react";
import { DEFAULT_RANGE_DAYS, formatDate, lastDays, RANGE_PRESETS } from "@po/shared";

type Range = { from: string; to: string };
type Ym = { y: number; m: number }; // m: 0–11

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
/** The first year the store holds records for (ARCHITECTURE §5). */
const FIRST_YEAR = 2015;

const iso = (y: number, m: number, d: number) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const ymOf = (date: string): Ym => ({ y: Number(date.slice(0, 4)), m: Number(date.slice(5, 7)) - 1 });
const addMonths = ({ y, m }: Ym, n: number): Ym => ({ y: y + Math.floor((m + n) / 12), m: (((m + n) % 12) + 12) % 12 });

/**
 * The date range filter (SPEC §6.3): quick ranges, then two months side by side. A click picks the
 * start, a second the end (either order); the range applies on the second click. Dates after
 * today are disabled: there is no data for them.
 */
export function DateRangePicker({ value, today, onChange }: { value: Range; today: string; onChange: (r: Range | null) => void }) {
  // The left month: the one holding the range's end, so the selection is in view on open.
  const [view, setView] = useState<Ym>(() => addMonths(ymOf(value.to), -1));
  const [anchor, setAnchor] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);

  const pick = (d: string) => {
    if (!anchor) return setAnchor(d);
    const [from, to] = anchor <= d ? [anchor, d] : [d, anchor];
    setAnchor(null);
    onChange({ from, to });
  };
  // While picking, the range runs from the first click to the hovered day.
  const shown: Range = anchor ? (hover && hover < anchor ? { from: hover, to: anchor } : { from: anchor, to: hover ?? anchor }) : value;
  const preset = (days: number) => {
    setAnchor(null);
    // The default range is no range in the URL, so a shared link keeps rolling with the days.
    onChange(days === DEFAULT_RANGE_DAYS ? null : lastDays(today, days));
  };
  const activePreset = RANGE_PRESETS.find((n) => {
    const r = lastDays(today, n);
    return r.from === value.from && r.to === value.to;
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {RANGE_PRESETS.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => preset(n)}
            aria-pressed={activePreset === n}
            className={`rounded-full border px-3.5 py-1.5 text-[13px] font-semibold ${activePreset === n ? "border-brand-2 bg-brand-2 text-white" : "border-line bg-page hover:bg-hover text-ink"}`}
          >
            Last {n} days
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4" onMouseLeave={() => setHover(null)}>
        <Month ym={view} onView={setView} today={today} shown={shown} anchor={anchor} onPick={pick} onHover={setHover} />
        <Month ym={addMonths(view, 1)} onView={(v) => setView(addMonths(v, -1))} today={today} shown={shown} anchor={anchor} onPick={pick} onHover={setHover} />
      </div>
      <p className="text-[12.5px] text-muted" role="status">
        {anchor ? `From ${formatDate(anchor)}: pick the end date` : `${formatDate(value.from)} – ${formatDate(value.to)}`}
      </p>
    </div>
  );
}

function Month({
  ym,
  onView,
  today,
  shown,
  anchor,
  onPick,
  onHover,
}: {
  ym: Ym;
  onView: (v: Ym) => void;
  today: string;
  shown: Range;
  anchor: string | null;
  onPick: (d: string) => void;
  onHover: (d: string) => void;
}) {
  const lastYear = Number(today.slice(0, 4)) + 1;
  const years = Array.from({ length: lastYear - FIRST_YEAR + 1 }, (_, i) => FIRST_YEAR + i);
  const days = new Date(Date.UTC(ym.y, ym.m + 1, 0)).getUTCDate();
  const lead = new Date(Date.UTC(ym.y, ym.m, 1)).getUTCDay();
  const cells: (string | null)[] = [...Array<null>(lead).fill(null), ...Array.from({ length: days }, (_, i) => iso(ym.y, ym.m, i + 1))];
  return (
    <div>
      <div className="flex justify-center gap-2 mb-2">
        <select aria-label="Month" className="input text-[14px]" value={ym.m} onChange={(e) => onView({ y: ym.y, m: Number(e.target.value) })}>
          {MONTHS.map((name, i) => (
            <option key={name} value={i}>
              {name}
            </option>
          ))}
        </select>
        <select aria-label="Year" className="input text-[14px]" value={ym.y} onChange={(e) => onView({ y: Number(e.target.value), m: ym.m })}>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
      <div role="grid" aria-label={`${MONTHS[ym.m]} ${ym.y}`} className="grid grid-cols-7 text-center text-[14px] tnum">
        {WEEKDAYS.map((w) => (
          <div key={w} role="columnheader" className="text-[12px] text-muted py-1.5">
            {w}
          </div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`pad-${i}`} />;
          const future = d > today;
          const edge = d === shown.from || d === shown.to;
          const inside = d > shown.from && d < shown.to;
          const isToday = d === today;
          return (
            <div key={d} className={`py-0.5 ${inside ? "bg-[color-mix(in_srgb,var(--brand)_12%,transparent)]" : ""}`}>
              <button
                type="button"
                disabled={future}
                onClick={() => onPick(d)}
                onMouseEnter={() => onHover(d)}
                aria-label={formatDate(d)}
                aria-pressed={edge}
                aria-current={isToday ? "date" : undefined}
                className={`mx-auto w-9 h-9 rounded-input flex items-center justify-center
                  ${edge ? "bg-brand-2 text-white font-bold" : future ? "text-muted opacity-40 cursor-not-allowed" : "hover:bg-hover text-ink"}
                  ${isToday && !edge ? "border border-ink font-bold text-[var(--danger)]" : ""}
                  ${anchor === d ? "ring-2 ring-brand-2 ring-offset-1" : ""}`}
              >
                {Number(d.slice(8))}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
